import { Radio, Stack, Typography } from "@mui/material";
import { GridLinkOperator } from "@mui/x-data-grid";

import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import LoopIcon from "@mui/icons-material/Loop";
import DoneIcon from "@mui/icons-material/Done";
import PendingIcon from "@mui/icons-material/Pending";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import RecyclingIcon from "@mui/icons-material/Recycling";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import SchoolIcon from "@mui/icons-material/School";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import CustomButton from "../../helper-components/input/CustomButton";
import CustomImage from "../../helper-components/misc/CustomImage";

import { formatText, convertToDate, formatDate } from "../shared/miscUtils.js";

// Default table style.
// Style the rows depending on class name.
export const defaultRowStyles = {};

export const defaultRowTheme = (props, params) => {
    return null;
};

// Initial state of the table.
// Data Grid will not react if you change the these values later on.
export const defaultInitialState = (options = {}) => {
    const { field = "id", sort = "desc" } = options.sorting || {};
    const { items = [], quickFilterValues = [] } = options.filter || {};

    return {
        sorting: {
            sortModel: [{ field, sort }]
        },
        filter: {
            filterModel: { items, quickFilterValues, quickFilterLogicOperator: GridLinkOperator.Or }
        },
        columns: {
            columnVisibilityModel: { id: false, ...(options.columnVisibility || {}) }
        }
    };
};

/**
 * Whether some row can be selected.
 *
 * Disable rows which have already been subscribed to.
 */
export const defaultIsRowSelectable = (props, params) => {
    return true;
};

/**
 * Controlled selection state.
 * NB! DataGrid uses ids.
 */
export const defaultRowSelection = (selected) => {
    return selected.map((row) => row.id);
};

/**
 * Controlled selection state.
 * Add the full object to the 'selected' state.
 */
export const defaultRowSetSelection = (setSelected, selected, rows) => {
    setSelected(rows.filter((item) => selected.includes(item.id)));
};

// Render functions.
const renderThumbnail = (props, params, options) => {
    const { thumbnail } = options;
    const src = typeof thumbnail === "function" ? thumbnail(props, params) : thumbnail;
    return src ? <CustomImage url={src} contain={true} /> : <QuestionMarkIcon />;
};

const renderType = (params) => {
    const type = params.row.type;
    const formatted = formatText(type);

    if (type === "PODCAST") {
        return <CustomButton icon={<PodcastsIcon className="cursor-default" />} tooltip={formatted} />;
    } else if (type === "SHORT") {
        return <CustomButton icon={<OndemandVideoIcon className="cursor-default" />} tooltip={formatted} />;
    } else if (type === "LECTURE") {
        return <CustomButton icon={<SchoolIcon className="cursor-default" />} tooltip={formatted} />;
    }

    return <QuestionMarkIcon />;
};

const renderSingleSelect = (props, params, options = {}) => {
    const { isRowSelectable = defaultIsRowSelectable } = options;
    const selectable = isRowSelectable(props, params);

    return (
        <Stack direction="row" alignItems="center">
            <Radio checked={props.selected.id === params.row.id} onChange={() => props.setSelected(params.row)} disabled={!selectable} color="primary" />
        </Stack>
    );
};

const renderStatus = (params, options = {}) => {
    const { queueIsProcessed = false } = options;

    const status = params.row.status;
    const formatted = formatText(status);

    if (status === "IN_QUEUE" && !queueIsProcessed) {
        return <CustomButton icon={<HourglassEmptyIcon className="spin cursor-default" />} tooltip={formatted} />;
    } else if (status === "IN_PROGRESS") {
        return <CustomButton icon={<LoopIcon className="spin cursor-default" />} tooltip={formatted} />;
    } else if (status === "AWAITING_APPROVAL") {
        return <CustomButton icon={<PendingIcon className="cursor-default" />} tooltip={formatted} />;
    } else if (status === "RECREATE") {
        return <CustomButton icon={<RecyclingIcon className="spin cursor-default" />} tooltip={formatted} />;
    } else if (status === "ERROR") {
        return <CustomButton icon={<ReportProblemIcon className="cursor-default" />} tooltip={formatted} />;
    } else if (status === "PROCESSED" || queueIsProcessed) {
        return <CustomButton icon={<DoneIcon className="cursor-default" />} tooltip={formatted} />;
    }

    return <CustomButton icon={<QuestionMarkIcon className="cursor-default" />} tooltip={formatted} />;
};

const renderButton = (props, params, options = {}) => {
    const { title = "View", onClick, disabled = false, endIcon = <ArrowForwardIcon />, size = "small", variant = "contained" } = options;

    return (
        <CustomButton
            {...props}
            title={typeof title === "function" ? title(props, params) : title}
            onClick={() => onClick(props, params)}
            disabled={typeof disabled === "function" ? disabled(props, params) : disabled}
            endIcon={endIcon}
            variant={variant}
            size={size}
            fullWidth
        />
    );
};

const renderText = (text = "N/A") => {
    return (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", maxHeight: "inherit", overflowY: "auto" }}>
            {text}
        </Typography>
    );
};

// Styles.
export const defaultStyle = { align: "center", headerAlign: "center", flex: 0.1, minWidth: 75, fontSize: 10 };
export const noEditStyle = { editable: false, selectable: false, sortable: false };

// Columns.
export const defaultId = { ...defaultStyle, field: "id", headerName: "ID" };
export const defaultName = { ...defaultStyle, field: "name", headerName: "Name", flex: 0.15, minWidth: 75 };
export const defaultDescription = {
    ...defaultStyle,
    field: "description",
    headerName: "Description",
    flex: 0.2,
    minWidth: 150
};
export const defaultStatus = (options) => {
    return {
        ...defaultStyle,
        field: "status",
        headerName: "Status",
        minWidth: 65,
        renderCell: (params) => renderStatus(params, options)
    };
};
export const defaultStatusMsg = {
    ...defaultStyle,
    field: "status_msg",
    headerName: "Status message",
    minWidth: 175
};
export const defaultType = {
    ...defaultStyle,
    field: "type",
    headerName: "Type",
    minWidth: 60,
    renderCell: (params) => renderType(params)
};
export const defaultTime = (key = "created_on") => {
    return {
        ...defaultStyle,
        field: key,
        type: "date",
        headerName: "Datetime",
        description: "Converted to local time.",
        align: "right",
        flex: 0.15,
        minWidth: 175,
        valueGetter: (params) => convertToDate(params.row[key]),
        valueFormatter: (params) => formatDate(params.value)
    };
};
export const defaultDate = (key = "created_on") => {
    return {
        ...defaultStyle,
        field: key,
        type: "date",
        headerName: "Date",
        description: "Converted to local date format.",
        flex: 0.15,
        minWidth: 175,
        valueGetter: (params) => convertToDate(params.row[key]),
        valueFormatter: (params) => formatDate(params.value, { onlyDate: true })
    };
};
export const defaultSingleSelect = (props) => {
    return {
        ...defaultStyle,
        field: "select",
        headerName: "Select",
        sortable: false,
        minWidth: 50,
        renderCell: (params) => renderSingleSelect(props, params)
    };
};
export const defaultThumbnail = (props, options) => {
    return {
        ...defaultStyle,
        ...noEditStyle,
        flex: 0.1,
        field: "thumbnail",
        headerName: "Thumbnail",
        description: "Preview image",
        minWidth: 100,
        renderCell: (params) => renderThumbnail(props, params, options)
    };
};
export const defaultButton = (props, options) => {
    return {
        ...defaultStyle,
        field: "button",
        headerName: "Action",
        description: "Click on the button",
        sortable: false,
        filterable: false,
        flex: 0.1,
        minWidth: 125,
        renderCell: (params) => renderButton(props, params, options)
    };
};
export const defaultText = (key = "text") => {
    return {
        ...defaultStyle,
        field: key,
        headerName: "Text",
        flex: 0.1,
        minWidth: 125,
        renderCell: (params) => renderText(params.row[key])
    };
};
